/**
 * A copy of the open document without review notes, to share.
 *
 * Reads the document as Word holds it (the writer's own edits included),
 * has the server take the regulator, language and reviewer notes out
 * (``POST /runs/{id}/clean-copy``), and opens the result as a new Word
 * document for the writer to save. Where Word cannot open a new document
 * from the add-in, the file is downloaded instead.
 */

import { downloadBlob, fetchCleanCopy } from "../api/client";

const SLICE_BYTES = 4 * 1024 * 1024;

/** The open document as a .docx Blob (``Document.getFileAsync``, compressed). */
export function readDocumentFile(): Promise<Blob> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(Office.FileType.Compressed, { sliceSize: SLICE_BYTES }, (res) => {
      if (res.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(res.error?.message ?? "Couldn't read the document"));
        return;
      }
      const file = res.value;
      const parts: Uint8Array[] = [];
      const next = (i: number) => {
        if (i >= file.sliceCount) {
          file.closeAsync();
          resolve(
            new Blob(parts as BlobPart[], {
              type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            }),
          );
          return;
        }
        file.getSliceAsync(i, (slice) => {
          if (slice.status !== Office.AsyncResultStatus.Succeeded) {
            file.closeAsync();
            reject(new Error(slice.error?.message ?? "Couldn't read the document"));
            return;
          }
          parts.push(new Uint8Array(slice.value.data as ArrayLike<number>));
          next(i + 1);
        });
      };
      next(0);
    });
  });
}

async function toBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/** Make the clean copy and open it. Answers how it was delivered. */
export async function openCleanCopy(runId: string): Promise<"opened" | "downloaded"> {
  const cleaned = await fetchCleanCopy(runId, await readDocumentFile());
  if (Office.context.requirements.isSetSupported("WordApi", "1.3")) {
    const base64 = await toBase64(cleaned);
    await Word.run(async (ctx) => {
      ctx.application.createDocument(base64).open();
      await ctx.sync();
    });
    return "opened";
  }
  downloadBlob(cleaned, "clean-copy.docx");
  return "downloaded";
}
